'use client';

import { cn } from '@/lib/utils';
import Image from 'next/image';

interface HeroDecorationProps {
  className?: string;
  /**
   * Opacity level for the decoration (0-100)
   * @default 15
   */
  opacity?: number;
  /**
   * Position of the decoration
   * @default 'right'
   */
  position?: 'right' | 'left' | 'center' | 'full';
  /**
   * Whether to mirror the decoration horizontally
   * @default false
   */
  mirror?: boolean;
  /**
   * Whether to show only on dark mode
   * @default false
   */
  darkOnly?: boolean;
}

/**
 * Hero Decoration Component
 *
 * A subtle, elegant background decoration for hero sections.
 * Uses the Maenda Media brand colors with flowing abstract lines.
 */
export function HeroDecoration({
  className,
  opacity = 15,
  position = 'right',
  mirror = false,
  darkOnly = false,
}: HeroDecorationProps) {
  const positionStyles = {
    right: 'right-0 left-auto',
    left: 'left-0 right-auto',
    center: 'left-1/2 -translate-x-1/2',
    full: 'left-0 right-0',
  };

  const widthStyles = {
    right: 'w-[70%] md:w-[60%] lg:w-[50%]',
    left: 'w-[70%] md:w-[60%] lg:w-[50%]',
    center: 'w-[90%] md:w-[80%]',
    full: 'w-full',
  };

  return (
    <div
      className={cn(
        'absolute top-0 bottom-0 pointer-events-none overflow-hidden -z-10',
        positionStyles[position],
        widthStyles[position],
        darkOnly && 'hidden dark:block',
        className
      )}
      style={{ opacity: opacity / 100 }}
      aria-hidden="true"
    >
      <Image
        src="/images/hero-decoration.svg"
        alt=""
        fill
        className={cn(
          'object-cover object-right-top',
          position === 'left' && 'object-left-top',
          position === 'center' && 'object-center',
          mirror && 'scale-x-[-1]'
        )}
        priority={false}
      />
    </div>
  );
}
